import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaPaperPlane, FaTimes, FaWhatsapp } from 'react-icons/fa';
import logoIA from '../../assets/logo-ia.png';
import { useAIChat } from '../../hooks/useAIChat';
import { WHATSAPP_SUPORTE_URL } from '../../services/constantes';

// ===== ANIMATIONS =====

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;

const dots = keyframes`
  0%, 20% { opacity: 0.3; }
  50% { opacity: 1; }
  100% { opacity: 0.3; }
`;

// ===== OVERLAY =====

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10010;
  animation: ${fadeIn} 0.15s ease;
`;

// ===== CHAT WINDOW =====

const ChatWindow = styled.div`
  width: 460px;
  max-width: calc(100vw - 32px);
  height: 560px;
  max-height: calc(100vh - 80px);
  background: #ffffff;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${scaleIn} 0.2s ease;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.3);

  @media (max-width: 480px) {
    width: 100vw;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
  }
`;

// ===== HEADER =====

const Header = styled.div`
  background: linear-gradient(135deg, #0f2b52 0%, #1F467E 50%, #2d6bc4 100%);
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LogoImg = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.25);
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeaderBrand = styled.span`
  font-size: 0.92rem;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: 0.2px;
`;

const HeaderSub = styled.span`
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.65);
  margin-top: 1px;
`;

const CloseBtn = styled.button`
  background: rgba(255,255,255,0.12);
  border: none;
  color: rgba(255,255,255,0.8);
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.12s;
  &:hover { background: rgba(255,255,255,0.22); color: #fff; }
`;

// ===== MESSAGES =====

const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  scrollbar-width: thin;
  scrollbar-color: #e5e7eb transparent;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 4px; }
`;

const MsgRow = styled.div<{ isUser: boolean }>`
  display: flex;
  justify-content: ${({ isUser }) => isUser ? 'flex-end' : 'flex-start'};
  align-items: flex-end;
  gap: 8px;
`;

const BotAvatar = styled.img`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const BotBubble = styled.div`
  background: #f9fafb;
  border: 1px solid #f0f0f0;
  border-radius: 4px 16px 16px 16px;
  padding: 12px 16px;
  font-size: 0.83rem;
  color: #1f2937;
  line-height: 1.65;
  max-width: 85%;
  white-space: pre-wrap;
  word-wrap: break-word;

  strong, b { color: #1F467E; font-weight: 600; }
`;

const UserBubble = styled.div`
  background: #1F467E;
  color: white;
  border-radius: 16px 4px 16px 16px;
  padding: 10px 16px;
  font-size: 0.83rem;
  line-height: 1.5;
  max-width: 80%;
`;

const WelcomeMsg = styled.div`
  text-align: center;
  padding: 32px 16px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const WelcomeLogo = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #e8ecf1;
`;

const WelcomeBrand = styled.h3`
  font-size: 1.05rem;
  font-weight: 700;
  color: #1F467E;
  margin: 0;
`;

const WelcomeText = styled.p`
  font-size: 0.84rem;
  color: #6b7280;
  margin: 0;
  max-width: 300px;
  line-height: 1.55;
`;

const WelcomeContext = styled.span`
  font-size: 0.72rem;
  color: #1F467E;
  background: #eef2f9;
  padding: 5px 14px;
  border-radius: 20px;
  font-weight: 500;
`;

const TypingBubble = styled.div`
  background: #f9fafb;
  border: 1px solid #f0f0f0;
  border-radius: 4px 16px 16px 16px;
  padding: 14px 18px;
  display: flex;
  gap: 5px;

  span {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #1F467E;
    opacity: 0.4;
    animation: ${dots} 1.4s infinite;
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
`;

// ===== INPUT =====

const InputArea = styled.div`
  padding: 14px 20px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  gap: 10px;
  align-items: center;
  flex-shrink: 0;
  background: #fafbfc;
`;

const Input = styled.input`
  flex: 1;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 11px 16px;
  font-size: 0.84rem;
  outline: none;
  background: #fff;
  transition: border-color 0.12s, box-shadow 0.12s;

  &:focus {
    border-color: #1F467E;
    box-shadow: 0 0 0 3px rgba(31, 70, 126, 0.08);
  }
  &::placeholder { color: #9ca3af; }
`;

const SendBtn = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #1F467E;
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.12s;

  &:hover { background: #17355F; }
  &:disabled { background: #d1d5db; cursor: not-allowed; }
`;

const FooterInfo = styled.div`
  text-align: center;
  padding: 6px 0 10px;
  font-size: 0.68rem;
  color: #9ca3af;
  display: flex;
  justify-content: center;
  gap: 8px;
`;

const WhatsAppBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  background: #25d366;
  color: white;
  border: none;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.15s;
  margin-top: 6px;
  margin-left: 34px;
  width: fit-content;

  &:hover {
    background: #1eba59;
    color: white;
    transform: translateY(-1px);
  }
`;

// ===== TRIGGER BUTTON =====

export const AITriggerButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: #1F467E;
  border: none;
  border-radius: 6px;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;

  &:hover {
    background: #17355F;
  }
`;

// ===== TYPES & HELPERS =====

interface ModalAIChatProps {
  contexto: string;
  onClose: () => void;
}

const fmt = (text: string) =>
  text.split('\n').map((line, i, arr) => (
    <span key={i}>
      {line.split(/\*\*(.*?)\*\*/g).map((part, j) =>
        j % 2 === 1 ? <strong key={j}>{part}</strong> : part
      )}
      {i < arr.length - 1 && <br />}
    </span>
  ));

// ===== COMPONENT =====

const ModalAIChat: React.FC<ModalAIChatProps> = ({ contexto, onClose }) => {
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { messages, isTyping, restantes, endRef, sendMessage } = useAIChat();

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const send = () => {
    const q = input.trim();
    if (!q || isTyping) return;
    setInput('');
    sendMessage(q, `[Contexto: O usuário está na tela "${contexto}"]`);
  };

  return (
    <Overlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ChatWindow>
        <Header>
          <HeaderLeft>
            <LogoImg src={logoIA} alt="LocaSmartPro" />
            <HeaderInfo>
              <HeaderBrand>LocaSmartPro</HeaderBrand>
              <HeaderSub>Assistente com Inteligência Artificial</HeaderSub>
            </HeaderInfo>
          </HeaderLeft>
          <CloseBtn onClick={onClose}><FaTimes size={14} /></CloseBtn>
        </Header>

        <MessagesArea>
          {messages.length === 0 && (
            <WelcomeMsg>
              <WelcomeLogo src={logoIA} alt="LocaSmartPro" />
              <WelcomeBrand>Assistente LocaSmartPro</WelcomeBrand>
              <WelcomeText>
                Pergunte qualquer coisa sobre o sistema e eu vou te ajudar!
              </WelcomeText>
              <WelcomeContext>{contexto}</WelcomeContext>
            </WelcomeMsg>
          )}

          {messages.map((msg, i) => (
            <React.Fragment key={i}>
              <MsgRow isUser={msg.type === 'user'}>
                {msg.type === 'user' ? (
                  <UserBubble>{msg.text}</UserBubble>
                ) : (
                  <>
                    <BotAvatar src={logoIA} alt="LocaSmartPro" />
                    <BotBubble>{fmt(msg.text)}</BotBubble>
                  </>
                )}
              </MsgRow>
              {msg.type === 'bot' && i === messages.length - 1 && !isTyping && (
                <WhatsAppBtn
                  href={WHATSAPP_SUPORTE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaWhatsapp size={14} />
                  Falar com Suporte Humano
                </WhatsAppBtn>
              )}
            </React.Fragment>
          ))}

          {isTyping && (
            <MsgRow isUser={false}>
              <BotAvatar src={logoIA} alt="LocaSmartPro" />
              <TypingBubble>
                <span /><span /><span />
              </TypingBubble>
            </MsgRow>
          )}
          <div ref={endRef} />
        </MessagesArea>

        <InputArea>
          <Input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && send()}
            placeholder="Pergunte algo sobre o LocaSmartPro..."
          />
          <SendBtn onClick={send} disabled={!input.trim() || isTyping}>
            <FaPaperPlane size={13} />
          </SendBtn>
        </InputArea>
        <FooterInfo>
          <span>Powered by LocaSmartPro AI</span>
          {restantes !== null && <span>|  {restantes} pergunta{restantes !== 1 ? 's' : ''} restante{restantes !== 1 ? 's' : ''} hoje</span>}
        </FooterInfo>
      </ChatWindow>
    </Overlay>
  );
};

export default ModalAIChat;
